import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import {
  Button,
  Text,
  useToast,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
  Box,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react';

import Card from 'components/card/Card';

import { useAppSelector, useAppDispatch } from 'hooks';

import {
  apiSaveLifeInsuranceForm,
  apiSubmitLifeInsuranceForm,
} from 'api';
import {
  setHasUnsavedChanges,
  changeIsDataSaved,
  setValidationErrors,
  clearValidationErrors,
  setWipStatus,
} from 'reducers/lifeInsuranceFormReducer';
import { WIP_STATUS_BINDER_PENDING_UPLOAD } from 'variables/constants';

interface LifeSubmitProps {
  isLocked?: boolean;
  canEditPremium?: boolean;
}

const LifeSubmit = ({ isLocked = false, canEditPremium = false }: LifeSubmitProps) => {
  const { uuid } = useParams();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const bgColor = useColorModeValue('white', 'gray.800');
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  const formData = useAppSelector((state) => state.lifeInsuranceForm.data);
  const hasUnsavedChanges = useAppSelector(
    (state) => state.lifeInsuranceForm.hasUnsavedChanges,
  );
  const isDataSaved = useAppSelector(
    (state) => state.lifeInsuranceForm.isDataSaved,
  );
  const missingMandatoryFields = useAppSelector(
    (state) => state.lifeInsuranceForm.missingMandatoryFields,
  );
  const premiumAmount = parseFloat(formData.premium_amount || '0');
  const premiumConfirmed = useAppSelector(
    (state) => state.lifeInsuranceForm.premiumConfirmed,
  );

  // Check if all required fields are filled
  const areRequiredFieldsFilled = useMemo(() => {
    return missingMandatoryFields.length === 0;
  }, [missingMandatoryFields]);

  // Check if premium confirmation is required and completed
  const isPremiumConfirmed = useMemo(() => {
    return premiumAmount <= 10000 || premiumConfirmed;
  }, [premiumAmount, premiumConfirmed]);

  // Check if form can be submitted
  const canSubmit = useMemo(() => {
    return areRequiredFieldsFilled && isDataSaved && isPremiumConfirmed;
  }, [areRequiredFieldsFilled, isDataSaved, isPremiumConfirmed]);

  // Helper to transform nested validation errors
  const transformValidationErrors = useCallback(
    (errors: Record<string, any>): Record<string, any> => {
      const transformed: Record<string, any> = {};

      Object.entries(errors).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          transformed[key] = value[0];
        } else if (typeof value === 'object' && value !== null) {
          transformed[key] = transformValidationErrors(value);
        } else {
          transformed[key] = value;
        }
      });

      return transformed;
    },
    [],
  );

  const handleSave = useCallback(async (showToast = true) => {
    setIsSaving(true);

    dispatch(clearValidationErrors());

    try {
      const dataToSave = {
        ...formData,
        premium_confirmed: premiumConfirmed,
      };
      const response = await apiSaveLifeInsuranceForm(uuid!, dataToSave);

      dispatch(changeIsDataSaved(true));
      dispatch(setHasUnsavedChanges(false));

      if (showToast) {
        toast({
          title: 'Success',
          description: response.detail || 'Life form data saved successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (error: any) {
      console.error('Failed to save life form:', error);
      const errorData = error?.response?.data || error;
      const hasValidationErrors =
        typeof errorData === 'object' &&
        errorData !== null &&
        Object.keys(errorData).length > 0;

      if (hasValidationErrors) {
        const transformedErrors = transformValidationErrors(errorData);
        dispatch(setValidationErrors(transformedErrors));

        if (showToast) {
          toast({
            title: 'Validation Error',
            description: 'Please fix the errors in the form and try again.',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top',
          });
        }
      } else {
        if (showToast) {
          toast({
            title: 'Error',
            description:
              errorData?.error ||
              error?.message ||
              'Failed to save form data. Please try again.',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top',
          });
        }
      }
    } finally {
      setIsSaving(false);
    }
  }, [uuid, formData, dispatch, toast, transformValidationErrors]);

  // Auto-save functionality: Save data 1 minute after it was last changed
  useEffect(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    if (!isDataSaved && !isLocked) {
      autoSaveTimerRef.current = setTimeout(() => {
        handleSave(false); // Auto-save without showing toast
      }, 60000); // 60 seconds
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [isDataSaved, isLocked, handleSave]);

  const handleSubmitForm = async () => {
    setIsSubmitting(true);

    try {
      const response = await apiSubmitLifeInsuranceForm(uuid!);

      dispatch(setWipStatus(WIP_STATUS_BINDER_PENDING_UPLOAD));

      toast({
        title: 'Success',
        description: response.detail || 'Form submitted successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to submit form. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const showErrors = !areRequiredFieldsFilled || !isDataSaved || !isPremiumConfirmed;

  return (
    <>
      {!isLocked && showErrors && (
        <Card mb="20px">
          <Flex gap="20px" direction="row">
            {!areRequiredFieldsFilled && (
              <Alert status="warning">
                <AlertIcon />
                <Box>
                  <AlertDescription>
                    All required fields must be filled
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            {!isDataSaved && (
              <Alert status="error">
                <AlertIcon />
                <Box>
                  <AlertDescription>
                    You have unsaved changes.
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            {!isPremiumConfirmed && (
              <Alert status="warning">
                <AlertIcon />
                <Box>
                  <AlertDescription>
                    Please confirm the premium amount is correct (exceeds $10,000)
                  </AlertDescription>
                </Box>
              </Alert>
            )}
          </Flex>
        </Card>
      )}

      {!isLocked && (
        <Card mb="20px">
          <Flex justify="flex-end">
            <Button
              onClick={handleSubmitForm}
              disabled={!canSubmit || isSubmitting}
              isLoading={isSubmitting}
              loadingText="Submitting..."
              colorScheme="brand"
              variant="solid"
              size="lg"
            >
              Submit Form
            </Button>
          </Flex>
        </Card>
      )}

      {(hasUnsavedChanges || !isDataSaved) && (!isLocked || canEditPremium) && (
        <Box
          position="fixed"
          bottom="20px"
          right="20px"
          zIndex={1000}
          maxW="500px"
        >
          <Card bg={bgColor} p={4} shadow="lg" borderWidth="1px">
            <HStack spacing={6}>
              <Text>You have unsaved changes</Text>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                isLoading={isSaving}
                loadingText="Saving..."
                colorScheme="yellow"
                variant="solid"
              >
                Save Changes
              </Button>
            </HStack>
          </Card>
        </Box>
      )}

      {isLocked && (
        <Card mb="20px">
          <Alert status="info">
            <AlertIcon />
            <Box>
              <AlertDescription>
                At this stage binder is no longer editable
              </AlertDescription>
            </Box>
          </Alert>
        </Card>
      )}
    </>
  );
};

export default LifeSubmit;
